"use client";

import * as React from "react";
import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";

import { cn } from "../../lib/utils";
import { IconChevronRight, IconCheck } from "@tabler/icons-react";

type ContextMenuSize = "sm" | "default" | "lg";

const contextMenuSizeStyles: Record<ContextMenuSize, string> = {
  sm: "p-0.5 [&_[data-slot$='-menu-label']]:px-1 [&_[data-slot$='-menu-label']]:py-0.5 [&_[data-slot$='-menu-label']]:text-caption [&_[data-slot$='-menu-label']]:leading-caption [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:gap-1 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:px-1 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:py-0.5 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:text-nav [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:leading-nav [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])_svg:not([class*='size-'])]:size-3.5 [&_[data-slot$='-menu-separator']]:mx-1 [&_[data-slot$='-menu-separator']]:my-0.5 [&_[data-slot$='-menu-shortcut']]:text-caption [&_[data-slot$='-menu-shortcut']]:leading-caption",
  default: "",
  lg: "p-2 [&_[data-slot$='-menu-label']]:px-2 [&_[data-slot$='-menu-label']]:py-1.5 [&_[data-slot$='-menu-label']]:text-sm [&_[data-slot$='-menu-label']]:leading-sm [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:gap-2 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:px-2 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:py-1.5 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:text-md [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:leading-md [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])_svg:not([class*='size-'])]:size-4.5 [&_[data-slot$='-menu-separator']]:mx-2 [&_[data-slot$='-menu-separator']]:my-1.5 [&_[data-slot$='-menu-shortcut']]:text-xs [&_[data-slot$='-menu-shortcut']]:leading-xs",
};

const contextMenuStyles = {
  trigger: "select-none",
  positioner: "z-floating isolate outline-none",
  content:
    "z-floating max-h-[var(--available-height)] min-w-36 origin-[var(--transform-origin)] overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-floating p-1 text-floating-fg shadow-none outline-none [animation-duration:var(--durations-fast)] [animation-timing-function:var(--easings-enter)] [transition-duration:var(--durations-faster)] data-[closed]:overflow-hidden data-[open]:[--sho-enter-scale:0.95] data-[closed]:[--sho-exit-scale:0.95] data-[side=bottom]:[--sho-enter-y:calc(var(--spacing-2)*-1)] data-[side=top]:[--sho-enter-y:var(--spacing-2)] data-[side=left]:[--sho-enter-x:var(--spacing-2)] data-[side=inline-start]:[--sho-enter-x:var(--spacing-2)] data-[side=right]:[--sho-enter-x:calc(var(--spacing-2)*-1)] data-[side=inline-end]:[--sho-enter-x:calc(var(--spacing-2)*-1)] data-[starting-style]:animate-[sho-enter] data-[ending-style]:animate-[sho-exit] focus-visible:shadow-none focus-visible:outline-none",
  label:
    "px-1.5 py-1 text-xs font-medium leading-xs text-fg-muted data-[inset]:pl-7",
  item: "relative flex cursor-default select-none items-center gap-1.5 rounded-md px-1.5 py-1 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[variant=destructive]:text-destructive data-[focus]:bg-accent data-[focus]:text-accent-foreground data-[variant=destructive]:data-[focus]:bg-[color-mix(in_srgb,var(--colors-destructive)_10%,transparent)] data-[variant=destructive]:data-[focus]:text-destructive focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dark:data-[variant=destructive]:data-[focus]:bg-[color-mix(in_srgb,var(--colors-destructive)_20%,transparent)]",
  subTrigger:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md px-1.5 py-1 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[open]:bg-accent data-[open]:text-accent-foreground data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  subContent: "w-auto min-w-24",
  checkboxItem:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md py-1 pl-1.5 pr-8 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  radioItem:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md py-1 pl-1.5 pr-8 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  itemIndicator:
    "pointer-events-none absolute right-2 flex items-center justify-center",
  separator: "mx-1.5 my-1 h-px bg-border",
  shortcut:
    "ml-auto text-xs leading-xs tracking-wide text-fg-muted [data-slot$='-menu-item']:focus_&:text-accent-foreground",
  chevron: "ml-auto",
};

const contextMenuItemSelector = [
  "[data-slot='context-menu-item']",
  "[data-slot='context-menu-sub-trigger']",
  "[data-slot='context-menu-checkbox-item']",
  "[data-slot='context-menu-radio-item']",
].join(",");

function focusNextContextMenuItem(
  menu: HTMLElement,
  currentTarget: EventTarget | null,
  backwards: boolean,
) {
  const items = Array.from(
    menu.querySelectorAll<HTMLElement>(contextMenuItemSelector),
  ).filter(
    (item) =>
      !item.hasAttribute("data-disabled") &&
      item.getAttribute("aria-disabled") !== "true",
  );

  if (items.length === 0) return;

  const currentIndex = items.findIndex((item) => item === currentTarget);
  const fallbackIndex = backwards ? 0 : -1;
  const nextIndex =
    (currentIndex === -1 ? fallbackIndex : currentIndex) + (backwards ? -1 : 1);
  items.at(nextIndex % items.length)?.focus();
}

function ContextMenu({ ...props }: ContextMenuPrimitive.Root.Props) {
  return <ContextMenuPrimitive.Root data-slot="context-menu" {...props} />;
}

function ContextMenuPortal({ ...props }: ContextMenuPrimitive.Portal.Props) {
  return (
    <ContextMenuPrimitive.Portal data-slot="context-menu-portal" {...props} />
  );
}

function ContextMenuTrigger({
  className,
  ...props
}: ContextMenuPrimitive.Trigger.Props) {
  return (
    <ContextMenuPrimitive.Trigger
      data-slot="context-menu-trigger"
      className={cn(
        contextMenuStyles.trigger,
        "[outline:none] focus-visible:[outline:none] focus-visible:[box-shadow:none]",
        className,
      )}
      {...props}
    />
  );
}

function ContextMenuContent({
  size = "default",
  className,
  align = "start",
  alignOffset = 4,
  side = "right",
  sideOffset = 0,
  onKeyDown,
  ...props
}: ContextMenuPrimitive.Popup.Props & {
  size?: ContextMenuSize;
} & Pick<
    ContextMenuPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Positioner
        className={contextMenuStyles.positioner}
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <ContextMenuPrimitive.Popup
          data-slot="context-menu-content"
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented || event.key !== "Tab") return;

            event.preventDefault();
            focusNextContextMenuItem(
              event.currentTarget,
              event.target,
              event.shiftKey,
            );
          }}
          className={cn(
            contextMenuStyles.content,
            contextMenuSizeStyles[size],
            "[outline:none] focus-visible:[outline:none] focus-visible:[box-shadow:none]",
            className,
          )}
          {...props}
        />
      </ContextMenuPrimitive.Positioner>
    </ContextMenuPrimitive.Portal>
  );
}

function ContextMenuGroup({ ...props }: ContextMenuPrimitive.Group.Props) {
  return (
    <ContextMenuPrimitive.Group data-slot="context-menu-group" {...props} />
  );
}

function ContextMenuLabel({
  className,
  inset,
  ...props
}: ContextMenuPrimitive.GroupLabel.Props & {
  inset?: boolean;
}) {
  return (
    <ContextMenuPrimitive.GroupLabel
      data-slot="context-menu-label"
      data-inset={inset}
      className={cn(contextMenuStyles.label, className)}
      {...props}
    />
  );
}

function ContextMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: ContextMenuPrimitive.Item.Props & {
  inset?: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <ContextMenuPrimitive.Item
      data-slot="context-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(contextMenuStyles.item, className)}
      {...props}
    />
  );
}

function ContextMenuSub({ ...props }: ContextMenuPrimitive.SubmenuRoot.Props) {
  return (
    <ContextMenuPrimitive.SubmenuRoot data-slot="context-menu-sub" {...props} />
  );
}

function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: ContextMenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean;
}) {
  return (
    <ContextMenuPrimitive.SubmenuTrigger
      data-slot="context-menu-sub-trigger"
      data-inset={inset}
      className={cn(contextMenuStyles.subTrigger, className)}
      {...props}
    >
      {children}
      <IconChevronRight className={contextMenuStyles.chevron} />
    </ContextMenuPrimitive.SubmenuTrigger>
  );
}

function ContextMenuSubContent({
  size = "default",
  className,
  ...props
}: React.ComponentProps<typeof ContextMenuContent>) {
  return (
    <ContextMenuContent
      data-slot="context-menu-sub-content"
      size={size}
      className={cn(contextMenuStyles.subContent, className)}
      side="right"
      {...props}
    />
  );
}

function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  inset,
  ...props
}: ContextMenuPrimitive.CheckboxItem.Props & {
  inset?: boolean;
}) {
  return (
    <ContextMenuPrimitive.CheckboxItem
      data-slot="context-menu-checkbox-item"
      data-inset={inset}
      className={cn(contextMenuStyles.checkboxItem, className)}
      checked={checked}
      {...props}
    >
      <span className={contextMenuStyles.itemIndicator}>
        <ContextMenuPrimitive.CheckboxItemIndicator>
          <IconCheck />
        </ContextMenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.CheckboxItem>
  );
}

function ContextMenuRadioGroup({
  ...props
}: ContextMenuPrimitive.RadioGroup.Props) {
  return (
    <ContextMenuPrimitive.RadioGroup
      data-slot="context-menu-radio-group"
      {...props}
    />
  );
}

function ContextMenuRadioItem({
  className,
  children,
  inset,
  ...props
}: ContextMenuPrimitive.RadioItem.Props & {
  inset?: boolean;
}) {
  return (
    <ContextMenuPrimitive.RadioItem
      data-slot="context-menu-radio-item"
      data-inset={inset}
      className={cn(contextMenuStyles.radioItem, className)}
      {...props}
    >
      <span className={contextMenuStyles.itemIndicator}>
        <ContextMenuPrimitive.RadioItemIndicator>
          <IconCheck />
        </ContextMenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.RadioItem>
  );
}

function ContextMenuSeparator({
  className,
  ...props
}: ContextMenuPrimitive.Separator.Props) {
  return (
    <ContextMenuPrimitive.Separator
      data-slot="context-menu-separator"
      className={cn(contextMenuStyles.separator, className)}
      {...props}
    />
  );
}

function ContextMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="context-menu-shortcut"
      className={cn(contextMenuStyles.shortcut, className)}
      {...props}
    />
  );
}

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
};
